import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink, useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Button,
  Divider,
  Paper,
  TextField,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GolfCourseIcon from "@material-ui/icons/GolfCourse";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import SearchIcon from "@material-ui/icons/Search";
import { STATES } from "../../constants/states";

const useStyles = makeStyles((theme) => ({
  hero: {
    padding: theme.spacing(8, 2, 6),
    textAlign: "center",
    background: "linear-gradient(150deg, #0F3D2E 0%, #092A1F 100%)",
    borderRadius: 12,
    marginBottom: theme.spacing(6),
    borderBottom: `3px solid ${theme.palette.secondary.main}`,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(5, 2, 4),
    },
  },
  heroEyebrow: {
    color: theme.palette.secondary.light,
    fontWeight: 700,
    fontSize: "0.85rem",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  heroTitle: {
    fontWeight: 800,
    lineHeight: 1.2,
    color: "#F7F4EC",
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down("sm")]: { fontSize: "2rem" },
  },
  heroSubtitle: {
    color: "rgba(247, 244, 236, 0.78)",
    fontSize: "1.1rem",
    maxWidth: 620,
    margin: "0 auto",
    lineHeight: 1.75,
    marginBottom: theme.spacing(4),
  },
  heroButtonGroup: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  heroSearchForm: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(3),
  },
  heroSearchInput: {
    width: "100%",
    maxWidth: 620,
    backgroundColor: "#FFFEFA",
    borderRadius: 45,
    "& .MuiOutlinedInput-root": {
      borderRadius: 45,
      fontSize: "1.05rem",
      [theme.breakpoints.down("xs")]: {
        fontSize: "0.95rem",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "16px 14px 16px 0",
      [theme.breakpoints.down("xs")]: {
        padding: "14px 10px 14px 0",
      },
    },
    "& fieldset": {
      border: "none",
    },
  },
  heroSearchBtn: {
    borderRadius: 45,
    padding: theme.spacing(1.1, 3),
    fontWeight: 700,
    textTransform: "none",
    marginLeft: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 2),
    },
  },
  primaryBtn: {
    borderRadius: 8,
    padding: theme.spacing(1.2, 3.5),
    fontWeight: 700,
    textTransform: "none",
    fontSize: "1rem",
  },
  outlineBtn: {
    borderRadius: 8,
    padding: theme.spacing(1.2, 3.5),
    fontWeight: 700,
    textTransform: "none",
    fontSize: "1rem",
    borderColor: "rgba(247, 244, 236, 0.55)",
    color: "#F7F4EC",
    "&:hover": {
      borderColor: "#F7F4EC",
      backgroundColor: "rgba(247, 244, 236, 0.08)",
    },
  },
  sectionLabel: {
    fontWeight: 700,
    fontSize: "0.8rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.75),
  },
  sectionSubtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
    maxWidth: 520,
  },
  articleCard: {
    height: "100%",
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    transition: "box-shadow 0.2s, transform 0.2s",
    "&:hover": {
      boxShadow: theme.shadows[5],
      transform: "translateY(-2px)",
    },
  },
  articleCardInner: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2.5),
  },
  articleCategory: {
    marginBottom: theme.spacing(1.5),
    fontSize: 11,
    height: 22,
  },
  articleTitle: {
    fontWeight: 700,
    fontSize: "1rem",
    lineHeight: 1.4,
    marginBottom: theme.spacing(1),
    flex: 1,
  },
  articleExcerpt: {
    color: theme.palette.text.secondary,
    fontSize: 13.5,
    lineHeight: 1.65,
    marginBottom: theme.spacing(2),
  },
  articleReadMore: {
    fontSize: 13,
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  toolCard: {
    padding: theme.spacing(3),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
    backgroundColor: "#FAF8F1",
  },
  toolIcon: {
    color: theme.palette.primary.main,
    fontSize: "2.2rem",
  },
  toolTitle: {
    fontWeight: 700,
    fontSize: "1rem",
  },
  toolDesc: {
    color: theme.palette.text.secondary,
    fontSize: 13.5,
    lineHeight: 1.6,
    flex: 1,
  },
  toolLink: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: 13,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  valueBar: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-around",
    padding: theme.spacing(4, 2),
    background: "#EFEBDF",
    borderRadius: 10,
    marginBottom: theme.spacing(6),
    gap: theme.spacing(2),
  },
  valueStat: {
    textAlign: "center",
    minWidth: 100,
  },
  valueNumber: {
    fontSize: "2rem",
    fontWeight: 800,
    color: theme.palette.primary.main,
    lineHeight: 1.1,
  },
  valueLabel: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    marginTop: 4,
  },
  section: {
    marginBottom: theme.spacing(7),
  },
  divider: {
    margin: theme.spacing(1, 0, 3),
  },
  featuredCard: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
  },
  featuredTitle: {
    fontWeight: 700,
    fontSize: "1.35rem",
    lineHeight: 1.35,
    marginBottom: theme.spacing(1.5),
  },
  featuredExcerpt: {
    color: theme.palette.text.secondary,
    fontSize: "0.95rem",
    lineHeight: 1.7,
    marginBottom: theme.spacing(2),
    maxWidth: 700,
  },
  stateRankingItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    textDecoration: "none",
    color: "inherit",
    transition: "background 0.15s",
    "&:hover": {
      background: theme.palette.action.hover,
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.25, 1.5),
    },
  },
  viewRankingsArrow: {
    fontSize: "0.82rem",
    color: theme.palette.primary.main,
    fontWeight: 600,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  viewRankingsFull: {
    display: "inline",
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  viewRankingsMobile: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "inline",
    },
  },
}));

// ── Featured articles: 6 hand-picked slugs + metadata ──────
const FEATURED_ARTICLES = [
  {
    slug: "slope-rating-by-region",
    title: "Golf Course Difficulty by Region: What Slope Ratings Reveal",
    category: "Research",
    excerpt:
      "Mountain courses, coastal links, and Florida flatlands don't play the same — and their slope ratings prove it. We break down what regional course data actually shows.",
  },
  {
    slug: "what-slope-rating-costs-you-in-strokes",
    title: "What Your Course's Slope Rating Actually Costs You in Strokes",
    category: "Research",
    excerpt:
      "A slope of 130 vs 113 is more than a number. For a 15-handicap golfer, it translates to a real, calculable difference in expected score. Here's the math.",
  },
  {
    slug: "budget-courses-with-high-slope-ratings",
    title: "High Slope, Low Price: How to Find Budget Courses with Championship Difficulty",
    category: "Value & Budget",
    excerpt:
      "The most interesting golf value isn't a cheap course — it's a genuinely difficult course that happens to be cheap. Here's how to find high-slope courses at recreational prices.",
  },
  {
    slug: "golf-as-a-mid-handicapper",
    title: "Golf as a Mid-Handicapper: What Course Reviews Never Tell You",
    category: "Mid-Handicapper",
    excerpt:
      "Golf media is written for beginners or scratch players. If you're a 10–20 handicap golfer, almost none of it applies to you. Here's what courses actually play like for the majority of golfers.",
  },
  {
    slug: "how-to-plan-a-golf-trip",
    title: "How to Plan a Golf Trip: Course Selection, Budget, and What to Skip",
    category: "Trip Planning",
    excerpt:
      "A golf trip planned around marketing hype is an expensive disappointment. Here's how to plan one around slope ratings, honest course data, and a realistic budget.",
  },
  {
    slug: "are-the-greens-fee-worth-it",
    title: "Are the Greens Fee Worth It? How to Evaluate Golf Course Value",
    category: "Mid-Handicapper",
    excerpt:
      "A $120 round and a $45 round can both be great or terrible value. Here's how to evaluate whether a course is actually worth the price — using the data on the scorecard.",
  },
  {
    slug: "back-nine-collapse",
    title: "The Back Nine Collapse: Why It Happens and How to Stop It",
    category: "Psychology",
    excerpt:
      "You play a solid front nine, get to the turn feeling good, then slowly unravel. This pattern has specific, identifiable causes — and practical fixes.",
  },
];

const TOOLS = [
  {
    icon: <TrendingUpIcon style={{ fontSize: "2.2rem", color: "#0F3D2E" }} />,
    title: "Handicap Calculator",
    description:
      "Enter your scores, course rating, and slope to instantly calculate your USGA Handicap Index and Course Handicap for any set of tees.",
    link: "/handicap-calculator",
    label: "Open Calculator →",
  },
  {
    icon: <MenuBookIcon style={{ fontSize: "2.2rem", color: "#0F3D2E" }} />,
    title: "Golf Glossary",
    description:
      "Every golf term explained in plain English — from albatross to scratch, slope to stroke play. A searchable reference for every level of golfer.",
    link: "/glossary",
    label: "Browse Glossary →",
  },
  {
    icon: <SearchIcon style={{ fontSize: "2.2rem", color: "#0F3D2E" }} />,
    title: "Course Finder",
    description:
      "Search 20,000+ golf courses. View full scorecards, slope ratings, course ratings, tee box yardages, green types, and community ratings.",
    link: "/courses",
    label: "Find Courses →",
  },
  {
    icon: <GolfCourseIcon style={{ fontSize: "2.2rem", color: "#0F3D2E" }} />,
    title: "Most Liked Courses",
    description:
      "See which courses the Ryze Golf community consistently rates highest. Real golfer opinions — no sponsored placements.",
    link: "/leaderboard",
    label: "View Leaderboard →",
  },
];

const LandingPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const [query, setQuery] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      history.push(`/courses/search?q=${encodeURIComponent(query.trim())}`);
    } else {
      history.push("/courses/search");
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Ryze Golf",
    url: "https://ryzegolf.com",
    description:
      "Golf insights, course data, and tools for real golfers. Original research, honest analysis, and a searchable database of 20,000+ courses.",
    publisher: {
      "@type": "Organization",
      name: "Ryze Golf",
      url: "https://ryzegolf.com",
      logo: {
        "@type": "ImageObject",
        url: "https://ryzegolf.com/logo192.png",
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Ryze Golf — Golf Insights, Course Data & Tools for Real Golfers</title>
        <meta
          name="description"
          content="Original golf research, honest course analysis, and free tools for recreational golfers. Handicap calculator, 20,000+ course database, and data-backed guides."
        />
        <link rel="canonical" href="https://ryzegolf.com/" />
        <meta property="og:title" content="Ryze Golf — Golf Insights, Course Data & Tools for Real Golfers" />
        <meta
          property="og:description"
          content="Original golf research, honest course analysis, and free tools for recreational golfers."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ryzegolf.com/" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ── Hero ─────────────────────────────────────────── */}
      <Box className={classes.hero}>
        <Typography className={classes.heroEyebrow}>
          20,000+ Courses · Scorecards · Slope & Course Ratings
        </Typography>
        <Typography variant="h3" component="h1" className={classes.heroTitle}>
          Find Your Next Course
        </Typography>
        <Typography className={classes.heroSubtitle}>
          Search the full RYZE database — scorecards, tee yardages, ratings, and
          maps for over 20,000 courses — backed by original research and honest
          analysis for the recreational golfer.
        </Typography>
        <form onSubmit={handleSearch} className={classes.heroSearchForm}>
          <TextField
            variant="outlined"
            className={classes.heroSearchInput}
            placeholder="Search courses..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputProps={{ "aria-label": "Search golf courses by name" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#0F3D2E" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            className={classes.heroSearchBtn}
          >
            Search
          </Button>
        </form>
        <div className={classes.heroButtonGroup}>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/courses"
            className={classes.outlineBtn}
          >
            Browse Rankings
          </Button>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/golf-tips"
            className={classes.outlineBtn}
          >
            Read Golf Insights
          </Button>
        </div>
      </Box>

      {/* ── Stats bar ────────────────────────────────────── */}
      <Box className={classes.valueBar}>
        <div className={classes.valueStat}>
          <Typography className={classes.valueNumber}>20,000+</Typography>
          <Typography className={classes.valueLabel}>Courses with Scorecards</Typography>
        </div>
        <div className={classes.valueStat}>
          <Typography className={classes.valueNumber}>50</Typography>
          <Typography className={classes.valueLabel}>State Rankings</Typography>
        </div>
        <div className={classes.valueStat}>
          <Typography className={classes.valueNumber}>Free</Typography>
          <Typography className={classes.valueLabel}>Tools & Calculators</Typography>
        </div>
        <div className={classes.valueStat}>
          <Typography className={classes.valueNumber}>100%</Typography>
          <Typography className={classes.valueLabel}>Independent</Typography>
        </div>
      </Box>

      {/* ── Featured Articles ────────────────────────────── */}
      <Box className={classes.section}>
        <Typography className={classes.sectionLabel}>From Our Research</Typography>
        <Typography variant="h5" className={classes.sectionTitle}>
          Golf Insights & Guides
        </Typography>
        <Typography variant="body1" className={classes.sectionSubtitle}>
          Original articles based on course data, handicap analysis, and
          real-world experience — written for golfers with a 10–25 handicap.
        </Typography>
        {/* Hero featured article */}
        <Card className={classes.featuredCard} elevation={0}>
          <CardActionArea
            component={RouterLink}
            to={`/golf-tips/${FEATURED_ARTICLES[0].slug}`}
            style={{ padding: "28px 28px 24px" }}
          >
            <Chip
              label={FEATURED_ARTICLES[0].category}
              size="small"
              className={classes.articleCategory}
              color="primary"
              variant="outlined"
            />
            <Typography className={classes.featuredTitle}>
              {FEATURED_ARTICLES[0].title}
            </Typography>
            <Typography className={classes.featuredExcerpt}>
              {FEATURED_ARTICLES[0].excerpt}
            </Typography>
            <Typography className={classes.articleReadMore}>Read article →</Typography>
          </CardActionArea>
        </Card>

        {/* Supporting articles grid */}
        <Grid container spacing={3}>
          {FEATURED_ARTICLES.slice(1).map((article) => (
            <Grid item xs={12} sm={6} md={4} key={article.slug}>
              <Card className={classes.articleCard} elevation={0}>
                <CardActionArea
                  component={RouterLink}
                  to={`/golf-tips/${article.slug}`}
                  className={classes.articleCardInner}
                  style={{ display: "flex", flexDirection: "column", height: "100%", padding: "20px" }}
                >
                  <Chip
                    label={article.category}
                    size="small"
                    className={classes.articleCategory}
                    color="primary"
                    variant="outlined"
                  />
                  <Typography className={classes.articleTitle}>
                    {article.title}
                  </Typography>
                  <Typography className={classes.articleExcerpt}>
                    {article.excerpt}
                  </Typography>
                  <Typography className={classes.articleReadMore}>
                    Read article →
                  </Typography>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box style={{ marginTop: 24 }}>
          <Button
            variant="outlined"
            color="primary"
            component={RouterLink}
            to="/golf-tips"
            style={{ borderRadius: 8, textTransform: "none", fontWeight: 700 }}
          >
            View all articles →
          </Button>
        </Box>
      </Box>

      <Divider className={classes.divider} />

      {/* ── Tools ───────────────────────────────────────── */}
      <Box className={classes.section} style={{ marginTop: 32 }}>
        <Typography className={classes.sectionLabel}>Free Tools</Typography>
        <Typography variant="h5" className={classes.sectionTitle}>
          Everything You Need at the Course
        </Typography>
        <Typography variant="body1" className={classes.sectionSubtitle}>
          Practical tools built around the same course data that powers our
          research — free, no account required.
        </Typography>
        <Grid container spacing={3}>
          {TOOLS.map((tool) => (
            <Grid item xs={12} sm={6} key={tool.title}>
              <Paper className={classes.toolCard} elevation={0}>
                {tool.icon}
                <Typography className={classes.toolTitle}>{tool.title}</Typography>
                <Typography className={classes.toolDesc}>{tool.description}</Typography>
                <RouterLink to={tool.link} className={classes.toolLink}>
                  {tool.label}
                </RouterLink>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Divider className={classes.divider} />

      {/* ── State Rankings ───────────────────────────────── */}
      <Box className={classes.section}>
        <Typography className={classes.sectionLabel}>State Rankings</Typography>
        <Typography variant="h5" className={classes.sectionTitle}>
          Best Public Golf Courses by State
        </Typography>
        <Typography variant="body1" className={classes.sectionSubtitle}>
          Curated editorial rankings of the top public and resort courses in major golf states — matched to our course database for full scorecard data.
        </Typography>
        <Grid container spacing={2}>
          {STATES.map(({ slug, label }) => (
            <Grid item xs={6} sm={3} key={slug}>
              <RouterLink to={`/best-golf-courses/${slug}`} className={classes.stateRankingItem}>
                <Typography style={{ fontWeight: 600, fontSize: "0.95rem" }}>{label}</Typography>
                <Typography className={classes.viewRankingsArrow}>
                  <span className={classes.viewRankingsFull}>View rankings →</span>
                  <span className={classes.viewRankingsMobile}>→</span>
                </Typography>
              </RouterLink>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Divider className={classes.divider} />

      {/* ── Editorial mission ────────────────────────────── */}
      <Box style={{ padding: "32px 0 48px", maxWidth: 680 }}>
        <Typography className={classes.sectionLabel}>Why Ryze Golf</Typography>
        <Typography variant="h5" className={classes.sectionTitle}>
          Built for the Golfer Who Wants to Understand the Game
        </Typography>
        <Typography variant="body1" style={{ lineHeight: 1.8, color: "inherit", marginBottom: 20 }}>
          Ryze Golf is an independent platform for recreational golfers — data-backed research, honest course analysis, and free tools, without sponsored rankings or affiliate revenue.
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          component={RouterLink}
          to="/about"
          style={{ borderRadius: 8, textTransform: "none", fontWeight: 700 }}
        >
          About Ryze Golf & Our Methodology →
        </Button>
      </Box>
    </>
  );
};

export default LandingPage;
